import CopyButton from 'components/Generics/Button/CopyButton';
import EditButton from 'components/Generics/Button/EditButton';
import Card from 'components/Generics/Card/Card';
import LoadingIndicator from 'components/Generics/Loader/LoadingIndicator';
import { IPoolProperties } from 'components/IdentityProviders/IdentityProviders';
import { FeatureFlagContext } from 'FeatureFlagContext';
import { useCallback, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import PoolParametersModal from './PoolParametersModal';
import * as S from './PoolParametersTabContent.styles';

export type ModalUserPools = {
    userPools: IPoolProperties[];
    currentUserPoolId: string;
}
export interface IPoolParametersTabProps {
    userPools: ModalUserPools;
    getUserPools: () => Promise<void>;
    isUserPoolModalOpen: boolean;
    setIsUserPoolModalOpen: (isOpen: boolean) => void;
    saveUserPoolSelection: (userPoolId: string) => Promise<void>;
    currentUserPool: IPoolProperties;
    poolPropertiesLoaded: boolean;
    userPoolsLoaded: boolean;
    fetchPoolProperties: () => Promise<void>;
}

const PoolParametersTabContent = ({ userPools, getUserPools, isUserPoolModalOpen, setIsUserPoolModalOpen, saveUserPoolSelection, currentUserPool, poolPropertiesLoaded, userPoolsLoaded, fetchPoolProperties }: IPoolParametersTabProps) => {
    const [newSelection, setNewSelection] = useState('');
    const { t } = useTranslation();
    const { multiUserPools } = useContext(FeatureFlagContext);

    const onCopied = useCallback((message) => {
        NotificationService.getInstance().notifyInfo(message);
    }, []);

    const copyText = useCallback((text, propertyName) => {
        const message = t('notifications:NOTIFY_SSO_POOL_PROPERTY_COPIED', { POOL_PROPERTY: propertyName });
        navigator.clipboard.writeText(text).then(() => {
            onCopied(message);
        });
    }, [t, onCopied]);

    const onEdit = async () => {
        await getUserPools();
        setNewSelection(currentUserPool.userPoolId);
        setIsUserPoolModalOpen(true);
    };

    const onCloseModal = async () => {
        if (userPools.currentUserPoolId !== currentUserPool.userPoolId) {
            await fetchPoolProperties();
        }
        setIsUserPoolModalOpen(false);
    };

    const onSaveModal = async (userPoolId: string) => {
        await saveUserPoolSelection(userPoolId);
    };

    return (
        <>
            <Card title={<Trans>POOL.TITLE</Trans>}>
                <S.PoolParameterContainer>
                    {multiUserPools && (
                        <S.Prop>
                            <S.PropName><Trans>POOL.PROP_NAME_SELECTED_POOL</Trans></S.PropName>
                            { poolPropertiesLoaded ?
                                <S.PropValue>
                                    <S.SelectedPoolNameWrapper>
                                        {currentUserPool.userPoolName}
                                    </S.SelectedPoolNameWrapper>
                                    <EditButton onClick={onEdit} />
                                </S.PropValue>
                                : <LoadingIndicator />}
                        </S.Prop>
                    )}
                    <S.Prop>
                        <S.PropName><Trans>POOL.PROP_NAME_ID</Trans></S.PropName>
                        <S.PoolPropertiesWrapper>
                            { poolPropertiesLoaded ?
                                <S.PropValue>{currentUserPool.userPoolId}
                                    <CopyButton onClick={() => copyText(currentUserPool.userPoolId, 'SSO POOL ID')} />
                                </S.PropValue>
                                : <LoadingIndicator />}
                        </S.PoolPropertiesWrapper>
                    </S.Prop>
                    <S.Prop>
                        <S.PropName><Trans>POOL.PROP_NAME_DOMAIN</Trans></S.PropName>
                        <S.PoolPropertiesWrapper>
                            { poolPropertiesLoaded?
                                <S.PropValue>{currentUserPool.userPoolSaml2Url}
                                    <CopyButton onClick={() => copyText(currentUserPool.userPoolSaml2Url, 'SSO POOL DOMAIN NAME')} />
                                </S.PropValue>
                                : <LoadingIndicator />}
                        </S.PoolPropertiesWrapper>
                    </S.Prop>
                </S.PoolParameterContainer>
            </Card>
            <S.ModalWrapper isOpen={isUserPoolModalOpen}>
                <PoolParametersModal
                    closeModal={onCloseModal}
                    saveModal={onSaveModal}
                    modalUserPools={userPools}
                    isOpen={isUserPoolModalOpen}
                    userPoolsLoaded={userPoolsLoaded}
                    newSelection={newSelection}
                    setNewSelection={setNewSelection}
                />
            </S.ModalWrapper>
        </>
    );
};

export default PoolParametersTabContent;
